import { ApiError, apiFetch } from './api-fetch.ts'

// The admin panel is deliberately JSON-first, same rationale as
// `registry-api.ts` — these interfaces cover the fields the panel itself
// reads/displays; anything else rides through as JSON.
export interface MemoryDocumentRow {
  id: string
  rootId: string
  scope: string
  refId: string
  name: string
  markdown: string
  version: number
  checksum: string
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

export interface SkillFileRow {
  id: string
  skillVersionId: string
  path: string
  content: string
  executable: boolean
  checksum: string
  createdAt: string
  [key: string]: unknown
}

export interface SkillVersionRow {
  id: string
  rootId: string
  skillDefinitionId: string
  version: number
  content: string
  checksum: string
  status: 'draft' | 'published'
  files: SkillFileRow[]
  createdAt: string
  [key: string]: unknown
}

export interface ListMemoryDocumentsFilter {
  rootId?: string
  scope?: string
  refId?: string
}

export async function listMemoryDocuments(
  filter?: ListMemoryDocumentsFilter,
): Promise<MemoryDocumentRow[]> {
  const params = new URLSearchParams()
  if (filter?.rootId) params.set('rootId', filter.rootId)
  if (filter?.scope) params.set('scope', filter.scope)
  if (filter?.refId) params.set('refId', filter.refId)
  const query = params.toString()
  const { items } = await apiFetch<{ items: MemoryDocumentRow[] }>(
    `/memory${query ? `?${query}` : ''}`,
  )
  return items
}

export async function getMemoryDocument(
  id: string,
): Promise<MemoryDocumentRow> {
  return apiFetch<MemoryDocumentRow>(`/memory/${id}`)
}

// The API upserts on (scope, refId, name) — a create with matching keys
// lands on the existing document rather than erroring.
export function createMemoryDocument(
  body: Record<string, unknown>,
): Promise<MemoryDocumentRow> {
  return apiFetch<MemoryDocumentRow>('/memory', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function patchMemoryDocument(
  id: string,
  body: Record<string, unknown>,
): Promise<MemoryDocumentRow> {
  return apiFetch<MemoryDocumentRow>(`/memory/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export async function deleteMemoryDocument(id: string): Promise<void> {
  await apiFetch<null>(`/memory/${id}`, { method: 'DELETE' })
}

export async function listSkillVersions(
  definitionId: string,
): Promise<SkillVersionRow[]> {
  const { items } = await apiFetch<{ items: SkillVersionRow[] }>(
    `/skills/${definitionId}/versions`,
  )
  return items
}

export function getOrCreateSkillDraft(
  definitionId: string,
): Promise<SkillVersionRow> {
  return apiFetch<SkillVersionRow>(`/skills/${definitionId}/draft`, {
    method: 'POST',
  })
}

export function updateSkillDraftContent(
  definitionId: string,
  content: string,
): Promise<SkillVersionRow> {
  return apiFetch<SkillVersionRow>(`/skills/${definitionId}/draft`, {
    method: 'PATCH',
    body: JSON.stringify({ content }),
  })
}

// The UI always sends `{approve: true}` — an operator clicking Publish IS
// the approval; the 409 `approval_required` path only exists for
// non-interactive/API callers that don't set it.
export function publishSkillVersion(
  versionId: string,
): Promise<SkillVersionRow> {
  return apiFetch<SkillVersionRow>(`/skills/versions/${versionId}/publish`, {
    method: 'POST',
    body: JSON.stringify({ approve: true }),
  })
}

export function createSkillFile(
  versionId: string,
  body: { path: string; content: string; executable: boolean },
): Promise<SkillFileRow> {
  return apiFetch<SkillFileRow>(`/skills/versions/${versionId}/files`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function updateSkillFile(
  versionId: string,
  fileId: string,
  patch: { path?: string; content?: string; executable?: boolean },
): Promise<SkillFileRow> {
  return apiFetch<SkillFileRow>(
    `/skills/versions/${versionId}/files/${fileId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    },
  )
}

export async function deleteSkillFile(
  versionId: string,
  fileId: string,
): Promise<void> {
  await apiFetch<null>(`/skills/versions/${versionId}/files/${fileId}`, {
    method: 'DELETE',
  })
}

// --- Skill assignments (Task 11/12) ---

export interface SkillAssignmentRow {
  id: string
  skillDefinitionId: string
  skillName: string
  enabled: boolean
}

export interface InheritedSkillAssignmentRow extends SkillAssignmentRow {
  inheritedFromScope: string
  inheritedFromName: string
}

export async function listSkillAssignments(
  scope: string,
  refId: string,
): Promise<{
  own: SkillAssignmentRow[]
  inherited: InheritedSkillAssignmentRow[]
}> {
  const params = new URLSearchParams({ scope, refId })
  return apiFetch<{
    own: SkillAssignmentRow[]
    inherited: InheritedSkillAssignmentRow[]
  }>(`/skills/assignments?${params}`)
}

export async function putSkillAssignments(
  scope: string,
  refId: string,
  definitionIds: string[],
): Promise<void> {
  await apiFetch<null>(`/skills/assignments/${scope}/${refId}`, {
    method: 'PUT',
    body: JSON.stringify({ definitionIds }),
  })
}

// --- conflict resolution (STC-19) ---

export interface MemoryEventRow {
  id: string
  documentId: string
  kind:
    'created' | 'updated' | 'delta_applied' | 'conflict' | 'conflict_resolved'
  fromVersion: number | null
  toVersion: number | null
  actor: string
  payload: Record<string, unknown> | null
  createdAt: string
  [key: string]: unknown
}

export async function listMemoryEvents(
  documentId: string,
): Promise<MemoryEventRow[]> {
  const { items } = await apiFetch<{ items: MemoryEventRow[] }>(
    `/memory/${documentId}/events`,
  )
  return items
}

/** `winner: 'local'` rewrites the doc from the preserved losing markdown;
 * `'server'` keeps the server content and (on the daemon's next push)
 * instructs it to discard its local copy. */
export function resolveMemoryConflict(
  documentId: string,
  winner: 'local' | 'server',
): Promise<MemoryDocumentRow> {
  return apiFetch<MemoryDocumentRow>(`/memory/${documentId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ winner }),
  })
}

// `ApiError` moved to `api-fetch.ts` with the shared fetch; re-exported here
// so existing importers of this module keep working.
export { ApiError }
